import { useState } from 'react'
import { Image, Maximize2, X } from 'lucide-react'
import photoDescriptions from '../data/photoDescriptions.json'

const PhotoGallery = () => {
  const [showAllPhotos, setShowAllPhotos] = useState(false)
  const [selectedPhoto, setSelectedPhoto] = useState(null)

  // Generate all start images from photoDescriptions.json
  const generateStartPhotos = () => {
    const photos = []

    // Get all image numbers from photoDescriptions and sort them numerically
    const imageNumbers = Object.keys(photoDescriptions)
      .map(num => parseInt(num))
      .sort((a, b) => a - b)

    imageNumbers.forEach(imageNum => {
      const description = photoDescriptions[imageNum.toString()]

      // Build filename in format: "start_NUMBER - Description.jpg" or "start_NUMBER.jpg"
      const filename = description && !description.startsWith('Historical Photo')
        ? `start_${imageNum} - ${description}.jpg`
        : `start_${imageNum}.jpg`

      photos.push({
        src: `/images/${filename}`,
        alt: description || `Lockridge Forest Historical Photo ${imageNum}`,
        description: description,
        index: imageNum
      })
    })

    return photos
  }

  const allPhotos = generateStartPhotos()
  const displayPhotos = showAllPhotos ? allPhotos : allPhotos.slice(0, 16) // Show 4x4 grid initially

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative text-white min-h-[400px] flex items-center"
        style={{backgroundImage: 'url(/images/club/image-5c5fe817.jpg)', backgroundSize: 'cover', backgroundPosition: 'center'}}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/80 via-tertiary-800/70 to-primary-900/80"></div>
        <div className="container-custom relative z-10 py-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6 animate-fade-in text-4xl md:text-5xl lg:text-6xl font-bold">
              Photo Gallery
            </h1>
            <p className="text-xl md:text-2xl mb-8 leading-relaxed opacity-95">
              Over 500 memories and moments from our beloved club spanning from the late 1970s through 2016
            </p>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="mb-4 text-gray-900">Historical Photo Archive</h2>
            <p className="text-lg text-gray-600">
              A visual journey through the decades of Lockridge Forest Swim Tennis Club
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {displayPhotos.map((photo, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow cursor-pointer group relative"
                onClick={() => setSelectedPhoto(photo)}
              >
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {photo.description && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                    <p className="text-white text-xs font-medium line-clamp-2">{photo.description}</p>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <Maximize2 className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            {!showAllPhotos && (
              <button
                onClick={() => setShowAllPhotos(true)}
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors shadow-md hover:shadow-lg mb-4"
              >
                <Maximize2 className="w-5 h-5" />
                View All {allPhotos.length} Photos
              </button>
            )}
            {showAllPhotos && (
              <button
                onClick={() => setShowAllPhotos(false)}
                className="inline-flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors shadow-md hover:shadow-lg mb-4"
              >
                Show Less
              </button>
            )}
            <div className="inline-flex items-center gap-2 bg-primary-50 border border-primary-200 rounded-lg px-6 py-3 mt-4">
              <Image className="w-5 h-5 text-primary-600" />
              <p className="text-sm text-primary-800">
                <span className="font-semibold">{allPhotos.length} historical photos</span> from our club's journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Lightbox Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedPhoto.src}
              alt={selectedPhoto.alt}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
            />
            {selectedPhoto.description && (
              <div className="bg-white/10 backdrop-blur-sm rounded-lg mt-4 p-4">
                <p className="text-white text-center text-lg font-medium">{selectedPhoto.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PhotoGallery
